/**
 * WarehouseReceivePage — Receive / create flow (Phase 3b-i)
 * ─────────────────────────────────────────────────────────
 * Creates a WarehouseItem (opens a stay) via manage_warehouse_item
 * action=receive. The headline complexity is the LINK DECISION branch
 * (ATTACHED / CREATED_UNVERIFIED / NONE), rendered with the {mode === … && (…)}
 * pattern. Role-gated to ADMIN/FIELD_REP (the warehouse-write roles).
 *
 * Fixed enums (tracking_mode, intake_mode, link_mode, put_away_now) use a local
 * segmented control — NOT ReferenceData (weight_unit precedent). asset_type and
 * the CREATED_UNVERIFIED `oem` come from ReferenceData. The backend sets
 * `verified` per link_mode and stamps received_date = today — neither is a form
 * field. No Owner / Condition / Charges fields (out of spec).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useUser } from '../../../shared/context/UserContext';
import { useReferenceData } from '../../../shared/hooks/useReferenceData';
import { ComboBox } from '../../../shared/components/ComboBox';
import { getSites } from '../../inventory/api/inventoryApi';
import { receiveWarehouseItem, getWarehouseItems } from '../api/warehouseApi';
import WarehouseToolPicker from '../components/WarehouseToolPicker';
import '../warehouse.css';

const WAREHOUSE_WRITE_ROLES = new Set(['ADMIN', 'FIELD_REP']);

function siteLabel(s) {
  if (s.site_city) return `${s.site_name} (${s.site_city})`;
  return s.site_name || 'Unnamed Site';
}

/* Local segmented control for fixed enums (weight_unit TapButtons precedent). */
function Seg({ value, onChange, options }) {
  return (
    <div className="wh-seg">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`wh-seg-btn${value === o.value ? ' wh-seg-active' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function WarehouseReceivePage() {
  const { callerEmail, userProfile } = useUser();
  const role = userProfile?.role;
  const navigate = useNavigate();

  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [sitesLoading, setSitesLoading] = useState(true);

  /* All caller-scoped sites (no module filter) — source-site list for the
     ATTACH tool picker, since a tool can live at any site, not just WAREHOUSE. */
  const [allSites, setAllSites] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  /* Form fields */
  const [assetType, setAssetType] = useState('');
  const [trackingMode, setTrackingMode] = useState('UNIQUE');
  const [quantity, setQuantity] = useState('1');
  const [contractId, setContractId] = useState('');
  const [customerPo, setCustomerPo] = useState('');
  const [intakeMode, setIntakeMode] = useState('MANIFEST');
  const [putAwayNow, setPutAwayNow] = useState(true);
  const [building, setBuilding] = useState('');
  const [section, setSection] = useState('');
  const [row, setRow] = useState('');
  const [stack, setStack] = useState('');

  /* Link decision */
  const [linkMode, setLinkMode] = useState('NONE');
  const [linkedToolId, setLinkedToolId] = useState('');
  const [linkedToolSiteId, setLinkedToolSiteId] = useState('');
  const [pickedSupplierToolId, setPickedSupplierToolId] = useState('');
  const [pickedSiteLabel, setPickedSiteLabel] = useState('');
  const [supplierToolId, setSupplierToolId] = useState('');
  const [oem, setOem] = useState('');

  /* Tools currently live in THIS warehouse (occupancy), for the picker gray-out.
     Keys on the warehouse site_id — NOT the picker's source-search site. */
  const [storedToolIds, setStoredToolIds] = useState(() => new Set());

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [toast, setToast] = useState(null);

  const { options: refOptions } = useReferenceData(['asset_type', 'oem'], callerEmail, siteId || null);
  const assetTypeOpts = refOptions.asset_type || [];
  const oemOpts = refOptions.oem || [];

  useEffect(() => {
    if (!callerEmail) return;
    setSitesLoading(true);
    getSites(callerEmail, 'WAREHOUSE')
      .then((data) => {
        const list = Array.isArray(data) ? data : data.sites || [];
        setSites(list);
        if (list.length === 1) setSiteId(list[0].site_id);
      })
      .catch(() => {})
      .finally(() => setSitesLoading(false));
  }, [callerEmail]);

  /* All scoped sites for the picker source list (no module → every site the
     caller can see). */
  useEffect(() => {
    if (!callerEmail) return;
    getSites(callerEmail)
      .then((data) => setAllSites(Array.isArray(data) ? data : data.sites || []))
      .catch(() => {});
  }, [callerEmail]);

  /* Warehouse occupancy → gray-out set. Default current-occupancy (no status
     override); keyed on the warehouse site_id. Refetches when the warehouse
     site changes. */
  useEffect(() => {
    if (!siteId || !callerEmail) { setStoredToolIds(new Set()); return; }
    getWarehouseItems(callerEmail, { siteId })
      .then((items) => setStoredToolIds(new Set(items.map((i) => i.linked_tool_id).filter(Boolean))))
      .catch(() => setStoredToolIds(new Set()));
  }, [siteId, callerEmail]);

  const closePicker = useCallback(() => setPickerOpen(false), []);
  const handlePickerConfirm = useCallback((picked) => {
    setLinkedToolId(picked.tool_id || '');
    setLinkedToolSiteId(picked.site_id || '');
    setPickedSupplierToolId(picked.supplier_tool_id || picked.tool_id || '');
    setPickedSiteLabel(picked.site_label || '');
    setErrors((prev) => ({ ...prev, attachTool: null }));
    setPickerOpen(false);
  }, []);

  /* When LOT, default WALK_UP keeps put_away_now true; nothing forced here —
     toggle is always offered. */
  const isLot = trackingMode === 'LOT';

  /* STORED-without-location guard (soft, client-side). Empty = all four tiers
     blank (trimmed). THE KNOB: the at-least-one rule is this .some() — make a
     tier mandatory by tightening it here. putAwayNow=true means "Store now". */
  const recvLocEmpty = ![building, section, row, stack].some((v) => v.trim());

  function validate() {
    const e = {};
    if (!siteId) e.siteId = 'Select a warehouse site.';
    if (!assetType) e.assetType = 'Asset type is required.';
    if (!contractId.trim()) e.contractId = 'Contract is required.';
    if (isLot) {
      const n = Number(quantity);
      if (!Number.isInteger(n) || n < 1) e.quantity = 'Quantity must be a whole number ≥ 1.';
    }
    if (linkMode === 'ATTACHED' && !(linkedToolId.trim() && linkedToolSiteId.trim())) {
      e.attachTool = 'Pick a tool to attach.';
    }
    if (putAwayNow && recvLocEmpty) {
      e.location = 'Add a location to store this item now.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  const canSubmit = useMemo(() => {
    if (!siteId || !assetType || !contractId.trim()) return false;
    if (isLot && !(Number.isInteger(Number(quantity)) && Number(quantity) >= 1)) return false;
    if (linkMode === 'ATTACHED' && (!linkedToolId.trim() || !linkedToolSiteId.trim())) return false;
    if (putAwayNow && recvLocEmpty) return false;
    return true;
  }, [siteId, assetType, contractId, isLot, quantity, linkMode, linkedToolId, linkedToolSiteId, putAwayNow, recvLocEmpty]);

  async function handleSubmit(ev) {
    ev.preventDefault();
    setSubmitError(null);
    if (!validate()) return;

    const payload = {
      site_id: siteId,
      asset_type: assetType,
      tracking_mode: trackingMode,
      quantity: isLot ? Number(quantity) : 1,
      contract_id: contractId.trim(),
      intake_mode: intakeMode,
      put_away_now: putAwayNow,
      building: building || null,
      section: section || null,
      row: row || null,
      stack: stack || null,
    };
    if (customerPo.trim()) payload.customer_po = customerPo.trim();
    if (linkMode === 'ATTACHED') {
      payload.linked_tool_id = linkedToolId.trim();
      payload.linked_tool_site_id = linkedToolSiteId.trim();
      // Denorm display fields from the picked tool (verbatim — backend stores as-is).
      if (pickedSupplierToolId) payload.linked_tool_supplier_id = pickedSupplierToolId;
      if (pickedSiteLabel) payload.linked_tool_site_name = pickedSiteLabel;
    } else if (linkMode === 'CREATED_UNVERIFIED') {
      payload.create_unverified = true;
      if (supplierToolId.trim()) payload.supplier_tool_id = supplierToolId.trim();
      if (oem.trim()) payload.oem = oem.trim();
    }

    setSubmitting(true);
    try {
      const res = await receiveWarehouseItem(callerEmail, payload);
      const id = res?.warehouse_item?.warehouse_item_id;
      setToast(`Received ${id || 'item'}.`);
      setTimeout(() => navigate('/warehouse/items'), 800);
    } catch (err) {
      setSubmitError(err.message || 'Receive failed.');
      setSubmitting(false);
    }
  }

  if (role && !WAREHOUSE_WRITE_ROLES.has(role)) {
    return (
      <div className="wh-lock">
        <Lock size={48} />
        <h1>You do not have access</h1>
      </div>
    );
  }

  return (
    <div className="wh-page">
      <div className="wh-header">
        <button className="wh-back" type="button" onClick={() => navigate('/warehouse/items')}>&larr; Stored Items</button>
        <h1 className="wh-header-title">Receive Item</h1>
      </div>

      <form className="wh-form" onSubmit={handleSubmit}>
        {submitError && <div className="wh-form-error">{submitError}</div>}
        {toast && <div className="wh-form-toast">{toast}</div>}

        {/* Site */}
        <label className="wh-field-row">
          <span className="wh-label wh-label-req">Warehouse Site</span>
          <select className="wh-input" value={siteId} onChange={(e) => setSiteId(e.target.value)} disabled={sitesLoading}>
            <option value="">Select a warehouse site…</option>
            {sites.map((s) => <option key={s.site_id} value={s.site_id}>{siteLabel(s)}</option>)}
          </select>
          {errors.siteId && <span className="wh-error-text">{errors.siteId}</span>}
        </label>

        {/* Asset type */}
        <label className="wh-field-row">
          <span className="wh-label wh-label-req">Asset Type</span>
          <select className="wh-input" value={assetType} onChange={(e) => setAssetType(e.target.value)}>
            <option value="">— Select —</option>
            {assetTypeOpts.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          {errors.assetType && <span className="wh-error-text">{errors.assetType}</span>}
        </label>

        {/* Tracking mode + qty */}
        <div className="wh-field-row">
          <span className="wh-label wh-label-req">Tracking</span>
          <Seg value={trackingMode} onChange={setTrackingMode}
            options={[{ value: 'UNIQUE', label: 'Unique' }, { value: 'LOT', label: 'Lot' }]} />
        </div>
        {isLot && (
          <label className="wh-field-row">
            <span className="wh-label wh-label-req">Quantity</span>
            <input className="wh-input wh-input-narrow" type="number" min="1" value={quantity}
              onChange={(e) => setQuantity(e.target.value)} />
            {errors.quantity && <span className="wh-error-text">{errors.quantity}</span>}
          </label>
        )}

        {/* Contract + PO */}
        <label className="wh-field-row">
          <span className="wh-label wh-label-req">Contract</span>
          <input className="wh-input" type="text" value={contractId} placeholder="e.g. ADL-2025-01"
            onChange={(e) => setContractId(e.target.value)} />
          {errors.contractId && <span className="wh-error-text">{errors.contractId}</span>}
        </label>
        <label className="wh-field-row">
          <span className="wh-label">Customer PO</span>
          <input className="wh-input" type="text" value={customerPo} onChange={(e) => setCustomerPo(e.target.value)} />
        </label>

        {/* Intake mode */}
        <div className="wh-field-row">
          <span className="wh-label wh-label-req">Intake</span>
          <Seg value={intakeMode} onChange={setIntakeMode}
            options={[{ value: 'MANIFEST', label: 'Manifest' }, { value: 'WALK_UP', label: 'Walk-up' }]} />
        </div>

        {/* Put away now */}
        <div className="wh-field-row">
          <span className="wh-label">Put away now</span>
          <Seg value={putAwayNow ? 'yes' : 'no'} onChange={(v) => setPutAwayNow(v === 'yes')}
            options={[{ value: 'yes', label: 'Store now (STORED)' }, { value: 'no', label: 'Stage inbound' }]} />
        </div>

        {/* Location */}
        <fieldset className="wh-fieldset">
          <legend className="wh-legend">Location {putAwayNow ? '(required to store now)' : '(optional while staged)'}</legend>
          <div className="wh-loc-grid">
            <input className="wh-input" placeholder="Building" value={building} onChange={(e) => setBuilding(e.target.value)} />
            <input className="wh-input" placeholder="Section" value={section} onChange={(e) => setSection(e.target.value)} />
            <input className="wh-input" placeholder="Row" value={row} onChange={(e) => setRow(e.target.value)} />
            <input className="wh-input" placeholder="Stack" value={stack} onChange={(e) => setStack(e.target.value)} />
          </div>
          {/* Live reason (the button is canSubmit-disabled before validate() runs,
              so drive the visible error from the live condition — mirrors the
              detail panel's {locEmpty && …} pattern). errors.location is also set
              in validate() for parity with the form's other fields. */}
          {putAwayNow && recvLocEmpty && (
            <span className="wh-error-text">Add a location to store this item now.</span>
          )}
          <span className="wh-hint">Uppercased on save.</span>
        </fieldset>

        {/* LINK DECISION */}
        <fieldset className="wh-fieldset">
          <legend className="wh-legend">Tool Link</legend>
          <Seg value={linkMode} onChange={setLinkMode}
            options={[
              { value: 'NONE', label: 'None' },
              { value: 'ATTACHED', label: 'Attach existing' },
              { value: 'CREATED_UNVERIFIED', label: 'Create unverified' },
            ]} />

          {linkMode === 'NONE' && (
            <p className="wh-hint">No tool association. Item is recorded verified.</p>
          )}

          {linkMode === 'ATTACHED' && (
            <div className="wh-link-fields">
              <p className="wh-hint">
                Links to an existing tool (at any site). No tool record is created. Item is
                recorded verified.
              </p>
              {linkedToolId && linkedToolSiteId ? (
                <div className="wh-attach-picked">
                  <span className="wh-chip">
                    {pickedSupplierToolId || linkedToolId}
                    <span className="wh-chip-from"> · from {pickedSiteLabel || linkedToolSiteId}</span>
                  </span>
                  <button type="button" className="wh-btn-secondary" onClick={() => setPickerOpen(true)}>Change</button>
                </div>
              ) : (
                <button type="button" className="wh-btn-secondary wh-find-tool-btn" onClick={() => setPickerOpen(true)}>
                  Find tool…
                </button>
              )}
              {errors.attachTool && <span className="wh-error-text">{errors.attachTool}</span>}
            </div>
          )}

          {linkMode === 'CREATED_UNVERIFIED' && (
            <div className="wh-link-fields">
              <p className="wh-hint">
                Spawns a new unverified tool at this warehouse site. Item is recorded NOT verified.
              </p>
              <label className="wh-field-row">
                <span className="wh-label">Supplier Tool ID</span>
                <input className="wh-input" type="text" value={supplierToolId}
                  onChange={(e) => setSupplierToolId(e.target.value)} />
              </label>
              <label className="wh-field-row">
                <span className="wh-label">OEM</span>
                <ComboBox value={oem} onChange={setOem} options={oemOpts}
                  placeholder="Select or enter OEM" className="wh-input" />
              </label>
            </div>
          )}
        </fieldset>

        <div className="wh-form-actions">
          <button type="button" className="wh-btn-secondary" onClick={() => navigate('/warehouse/items')}>Cancel</button>
          <button type="submit" className="wh-btn-primary" disabled={!canSubmit || submitting}>
            {submitting ? 'Receiving…' : 'Receive'}
          </button>
        </div>
      </form>

      {pickerOpen && (
        <WarehouseToolPicker
          warehouseSiteId={siteId}
          callerEmail={callerEmail}
          sites={allSites}
          storedToolIds={storedToolIds}
          onConfirm={handlePickerConfirm}
          onCancel={closePicker}
        />
      )}
    </div>
  );
}
